import React from 'react';
import cn from 'clsx';

import { Button, UIHeading2, UIText2, useDeviceType } from '@cian/ui-kit';

import { IButton, IUniqueOffers } from 'shared/types/common/offer';
import { ETestIds } from 'shared/constants/testIds';
import { Card } from './components/Card';
import { BankLogoList } from '../BankLogoList';

import { ShadowWrapper } from '../ShadowWrapper';

import styles from './styles.css';
import { setAuthorizedModal } from '../../redux/landingSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAuthorized } from '../../selectors/user';
import { useTracking } from '../../utils/tracking/useTracking';

export interface IPersonalizedOfferProps {
  logoPaths: string[];
  header: string;
  description: string;
  button: IButton;
  usp: IUniqueOffers[];
  placeOnList: number;
}

export const PersonalizedOffer: React.FC<IPersonalizedOfferProps> = ({
  logoPaths,
  header,
  description,
  button: { text, path, theme, authorizationRequired, bankName, analyticEventKey },
  usp,
}) => {
  const deviceType = useDeviceType();
  const dispatch = useDispatch();
  const { trackRedirect, trackClickOnOfferButton } = useTracking();

  const isAuthorized = useSelector(selectIsAuthorized);

  const handleClick = () => {
    trackClickOnOfferButton(null);
    if (!isAuthorized && authorizationRequired) {
      dispatch(setAuthorizedModal({ isOpen: true, pathForRedirect: path, typeForRedirect: analyticEventKey }));

      return;
    }

    trackRedirect(bankName, analyticEventKey);
    window.open(path, '_self');
  };

  return (
    <ShadowWrapper>
      <div className={cn(styles['root'], styles[deviceType])} data-testid={ETestIds.PersonalizedOffer}>
        <div className={styles['top']}>
          <BankLogoList banks={logoPaths} />
          <div className={cn(styles['button'], styles[deviceType])}>
            <Button theme={theme} onClick={handleClick} fullWidth>
              {text}
            </Button>
          </div>
        </div>
        <div className={styles['description']}>
          <UIHeading2>{header}</UIHeading2>
          <UIText2>{description}</UIText2>
        </div>
        <div className={cn(styles['cards'], styles[deviceType])}>
          {usp.map(({ iconPath, header, description }, index) => {
            return <Card key={index} title={header} text={description} imgPath={iconPath} deviceType={deviceType} />;
          })}
        </div>
      </div>
    </ShadowWrapper>
  );
};

PersonalizedOffer.displayName = 'PersonalizedOffer';
